/**
 * Normalizes AI/dynamic replay WebSocket payloads to the same top-level field names and
 * nesting as non-dynamic replay (`replayStepStart`, `replayStepComplete`, `replayComplete`).
 *
 * Tool argument *values* are not transformed — only keys are coalesced to `args` / `result`
 * so the rest of the app can stay branch-free except where you intentionally handle arg shape.
 */

function parseStepIndex(raw) {
  if (raw == null || typeof raw !== 'object') return NaN;
  const explicitIndex =
    raw.stepIndex ??
    raw.step_index ??
    raw.index ??
    raw.flatIndex ??
    raw.flat_index ??
    raw.currentStepIndex ??
    raw.current_step_index ??
    raw.currentIndex ??
    raw.current_index;
  if (explicitIndex != null) {
    const n = typeof explicitIndex === 'number' ? explicitIndex : Number(explicitIndex);
    return Number.isFinite(n) ? n : NaN;
  }

  const v =
    raw.stepNumber;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? Math.max(n - 1, 0) : NaN;
}

function mapReplayStepStatus(rawStatus, rawOutcome) {
  const s = String(rawStatus ?? '').toLowerCase();
  if (['completed', 'complete', 'success', 'succeeded', 'passed', 'done', 'ok'].includes(s)) {
    return 'completed';
  }
  if (['failed', 'failure', 'error', 'rejected'].includes(s)) {
    return 'failed';
  }
  if (['skipped', 'skip'].includes(s)) {
    return 'skipped';
  }
  if (['executing', 'running', 'in_progress', 'in-progress', 'pending'].includes(s)) {
    return 'executing';
  }
  const o = String(rawOutcome ?? '').toLowerCase();
  if (['success', 'succeeded', 'passed', 'completed', 'complete'].includes(o)) {
    return 'completed';
  }
  if (['failed', 'failure', 'error'].includes(o)) {
    return 'failed';
  }
  if (rawStatus) return rawStatus;
  return 'completed';
}

/**
 * @param {object} data raw `replayStepStart` payload
 * @returns {object} same shape as non-dynamic: stepIndex, tool, args, …
 */
export function normalizeReplayStepStart(data) {
  if (!data || typeof data !== 'object') return data;
  const stepIndex = parseStepIndex(data);
  const tool = data.tool ?? data.toolName ?? data.name ?? '';
  const argsFromArgs = data.args && typeof data.args === 'object' ? data.args : {};
  const argsFromArguments = data.arguments && typeof data.arguments === 'object' ? data.arguments : {};
  const argsFromInput = data.input && typeof data.input === 'object' ? data.input : {};
  const args = { ...argsFromArguments, ...argsFromInput, ...argsFromArgs };

  return {
    ...data,
    stepIndex,
    tool,
    args,
  };
}

/**
 * @param {object} payload raw `replayStepComplete` payload
 * @returns {object} same shape as non-dynamic: stepIndex, status, screenshot, result (with .success)
 */
export function normalizeReplayStepComplete(payload) {
  if (!payload || typeof payload !== 'object') return payload;

  const stepIndex = parseStepIndex(payload);
  let status = mapReplayStepStatus(payload.status, payload.outcome ?? payload.phase);

  const screenshot =
    payload.screenshot ??
    payload.screenShot ??
    payload.screenshotUrl ??
    payload.screenshotPath;

  let resultBody =
    payload.result ??
    payload.executionResult ??
    payload.toolResult ??
    payload.tool_output ??
    payload.output;

  if (resultBody != null && typeof resultBody !== 'object') {
    resultBody = { value: resultBody };
  }
  if (resultBody == null || typeof resultBody !== 'object') {
    resultBody = {};
  }

  const successFromPayload = typeof payload.success === 'boolean' ? payload.success : undefined;
  const successFromFlat = typeof resultBody.success === 'boolean' ? resultBody.success : undefined;
  const nested = resultBody.result;
  const successFromNested =
    nested && typeof nested === 'object' && typeof nested.success === 'boolean'
      ? nested.success
      : undefined;

  if (typeof resultBody.success !== 'boolean') {
    const coalesced = successFromFlat ?? successFromNested ?? successFromPayload;
    if (typeof coalesced === 'boolean') {
      resultBody = { ...resultBody, success: coalesced };
    }
  }

  if (status === 'completed' && resultBody.success === false) {
    status = 'failed';
  }

  return {
    ...payload,
    stepIndex,
    status,
    screenshot,
    result: resultBody,
  };
}

/**
 * @param {object} data raw `replayComplete` payload
 * @returns {object} `success` + `results.summary.{completed,failed,skipped}` like non-dynamic
 */
export function normalizeReplayCompletePayload(data) {
  if (!data || typeof data !== 'object') return data;

  let results = data.results;
  if (!results || typeof results !== 'object') {
    return {
      ...data,
      success: typeof data.success === 'boolean' ? data.success : false,
      results: {
        summary: { completed: 0, failed: 0, skipped: 0 },
      },
    };
  }

  const summary = results.summary && typeof results.summary === 'object' ? results.summary : null;
  if (!summary) {
    return {
      ...data,
      success: !!data.success,
      results: {
        ...results,
        summary: { completed: 0, failed: 0, skipped: 0 },
      },
    };
  }

  const normalizedResults = {
    ...results,
    summary: {
      completed: summary.completed ?? summary.passed ?? summary.success ?? 0,
      failed: summary.failed ?? summary.errors ?? 0,
      skipped: summary.skipped ?? 0,
    },
  };

  return {
    ...data,
    success: typeof data.success === 'boolean' ? data.success : true,
    results: normalizedResults,
  };
}
